/**
 * 权益管理 Store (Pinia)
 * 
 * 统一管理用户权益信息，包括额度、套餐、权益等
 * 全局可访问，支持实时更新
 * 
 * @version 1.0.0
 * @author Frontend Team
 * @since 2025-10-23
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useUserStore } from './user'
import { type Package, type UserPackage } from '@/api/package'
import { authApi, type UserUsageInfo, type PackageSummary } from '@/api/auth'
import { entitlementApi } from '@/api/entitlement'

/**
 * 额度信息
 */
export interface QuotaInfo {
  total: number
  used: number
  remaining: number
  userTier: string
  quotaType: 'free' | 'paid'
  expireTime?: string
}

/**
 * 权益配置
 */
export interface EntitlementConfig {
  task: {
    maxConcurrent: number
    maxBatchSize: number
    maxDailyTasks: number
  }
  conversion: {
    maxTextLength: number
    maxDailyChars: number
  }
}

/**
 * 权益状态
 */
export interface EntitlementState {
  quota: QuotaInfo
  package: Package | null
  isLoading: boolean
  error: string | null
  lastUpdated: number
}

export const useEntitlementStore = defineStore('entitlement', () => {
  // ============= 状态定义 =============

  const quota = ref<QuotaInfo>({
    total: 0,
    used: 0,
    remaining: 0,
    userTier: 'guest',
    quotaType: 'free'
  })

  const currentPackage = ref<UserPackage | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const lastUpdated = ref(0)

  // 完整的使用状态信息（用于配额显示组件）
  const usageInfo = ref<UserUsageInfo | null>(null)

  // 主套餐摘要信息（后端选择的优先级最高的套餐）
  const packageSummary = ref<PackageSummary | null>(null)

  // 权益配置信息（包含单次字符上限等）
  const entitlementConfig = ref<EntitlementConfig | null>(null)

  // 请求去重：防止并发请求
  let fetchAllPromise: Promise<void> | null = null
  let fetchEntitlementConfigPromise: Promise<void> | null = null

  // ============= 计算属性 =============
  
  const userStore = useUserStore()
  const isLoggedIn = computed(() => userStore.isLoggedIn)

  /**
   * 额度使用百分比
   */
  const percentage = computed(() => {
    if (quota.value.total === 0) return 0
    return Math.round((quota.value.used / quota.value.total) * 100)
  })

  /**
   * 额度是否低（>= 90%）
   */
  const isLow = computed(() => percentage.value >= 90)

  /**
   * 额度是否警告（70% - 90%）
   */
  const isWarning = computed(() => percentage.value >= 70 && percentage.value < 90)

  /**
   * 是否有付费套餐
   */
  const hasPaidPackage = computed(() => {
    return currentPackage.value !== null && quota.value.quotaType === 'paid'
  })

  /**
   * 用户层级标签
   */
  const tierLabel = computed(() => {
    const labels: Record<string, string> = {
      guest: '游客',
      free: '免费用户',
      basic: '基础版',
      pro: '专业版',
      enterprise: '企业版',
      expired: '已过期'
    }
    return labels[quota.value.userTier] || '未知'
  })

  /**
   * 额度类型标签
   */
  const quotaTypeLabel = computed(() => {
    if (quota.value.quotaType === 'free') {
      return isLoggedIn.value ? '免费额度（每日重置）' : '游客额度（每日重置）'
    }
    return '付费额度（套餐内）'
  })

  /**
   * 单次请求字符上限（从权益配置获取）
   */
  const singleRequestLimit = computed(() => {
    // 登录用户：从后端获取的权益配置中读取
    if (isLoggedIn.value && entitlementConfig.value?.conversion?.maxTextLength) {
      return entitlementConfig.value.conversion.maxTextLength
    }

    // 未登录用户（游客）：默认 500
    if (!isLoggedIn.value) {
      return 500
    }

    // 登录用户但后端未返回配置：默认 1000
    return 1000
  })

  // ============= 方法 =============

  /**
   * 初始化权益信息
   */
  const initialize = async () => {
    await fetchAll()
  }

  /**
   * 获取所有权益信息（带去重）
   */
  const fetchAll = async () => {
    // 如果已有正在进行的请求，直接返回该 Promise
    if (fetchAllPromise) {
      return fetchAllPromise
    }

    isLoading.value = true
    error.value = null

    fetchAllPromise = (async () => {
      try {
        if (isLoggedIn.value) {
          await fetchUserQuota()
          // 登录用户获取权益配置
          await fetchEntitlementConfig()
        } else {
          await fetchGuestQuota()
        }
        lastUpdated.value = Date.now()
      } catch (err: any) {
        error.value = err.message || '获取权益信息失败'
        console.error('[EntitlementStore] 获取权益信息失败:', err)
      } finally {
        isLoading.value = false
        fetchAllPromise = null // 请求完成后清除
      }
    })()

    return fetchAllPromise
  }

  /**
   * 获取登录用户额度
   */
  const fetchUserQuota = async () => {
    try {
      // 只调用 usage-status 接口，已包含套餐摘要信息
      const usageStatus = await authApi.getUserUsageStatus()

      // 保存完整的 usageInfo 数据
      usageInfo.value = usageStatus.usageInfo

      // 保存主套餐摘要信息
      packageSummary.value = usageStatus.packageSummary

      if (usageStatus.packageSummary) {
        // 有付费套餐 - 使用 usage-status 返回的数据
        const now = new Date();
        const expireDate = new Date(usageStatus.packageSummary.expiresAt);
        const status = expireDate > now ? 'active' : 'expired';

        // 注意：这里只保存套餐摘要信息，不是完整套餐详情
        currentPackage.value = {
          package: {
            id: usageStatus.packageSummary.packageId,
            name: usageStatus.packageSummary.name,
            versionCode: usageStatus.packageSummary.versionCode,
            versionName: usageStatus.packageSummary.versionName,
            // 其他字段在需要时从 /api/v1/user/package 获取
          } as any,
          expireTime: usageStatus.packageSummary.expiresAt,
          credits: usageStatus.packageSummary.remainingCredits,
          status: status
        }

        quota.value = {
          total: usageStatus.usageInfo.currentLimit,
          used: usageStatus.usageInfo.currentUsed,
          remaining: usageStatus.usageInfo.remainingCurrent,
          userTier: usageStatus.userTier,
          quotaType: usageStatus.usageInfo.quotaType as 'free' | 'paid',
          expireTime: usageStatus.packageSummary.expiresAt
        }
      } else {
        // 没有套餐，使用免费额度
        currentPackage.value = null

        quota.value = {
          total: usageStatus.usageInfo.currentLimit,
          used: usageStatus.usageInfo.currentUsed,
          remaining: usageStatus.usageInfo.remainingCurrent,
          userTier: usageStatus.userTier,
          quotaType: 'free'
        }
      }
    } catch (err) {
      console.error('[EntitlementStore] 获取用户额度失败:', err)
      currentPackage.value = null
      usageInfo.value = null
      setDefaultFreeQuota()
    }
  }

  /**
   * 获取游客额度
   */
  const fetchGuestQuota = async () => {
    try {
      const usageStatus = await authApi.getUserUsageStatus()

      if (usageStatus && usageStatus.usageInfo) {
        // 保存完整的 usageInfo 数据
        usageInfo.value = usageStatus.usageInfo

        quota.value = {
          total: usageStatus.usageInfo.currentLimit,
          used: usageStatus.usageInfo.currentUsed,
          remaining: usageStatus.usageInfo.remainingCurrent,
          userTier: 'guest',
          quotaType: 'free'
        }
      } else {
        // 默认游客额度
        setDefaultGuestQuota()
      }
    } catch (err) {
      console.error('[EntitlementStore] 获取游客额度失败:', err)
      usageInfo.value = null
      setDefaultGuestQuota()
    }
  }

  /**
   * 获取权益配置（包含单次字符上限等，带去重）
   */
  const fetchEntitlementConfig = async () => {
    // 如果已有正在进行的请求，直接返回该 Promise
    if (fetchEntitlementConfigPromise) {
      return fetchEntitlementConfigPromise
    }

    fetchEntitlementConfigPromise = (async () => {
      try {
        const config = await entitlementApi.getEntitlements()
        entitlementConfig.value = config
      } catch (err) {
        console.error('[EntitlementStore] 获取权益配置失败:', err)
        entitlementConfig.value = null
      } finally {
        fetchEntitlementConfigPromise = null // 请求完成后清除
      }
    })()

    return fetchEntitlementConfigPromise
  }



  /**
   * 设置默认免费额度
   */
  const setDefaultFreeQuota = () => {
    quota.value = {
      total: 10000,
      used: 0,
      remaining: 10000,
      userTier: 'free',
      quotaType: 'free'
    }
  }

  /**
   * 设置默认游客额度
   */
  const setDefaultGuestQuota = () => {
    quota.value = {
      total: 5000,
      used: 0,
      remaining: 5000,
      userTier: 'guest',
      quotaType: 'free'
    }
  }

  /**
   * 刷新权益信息
   */
  const refresh = async () => {
    await fetchAll()
  }

  /**
   * 消费额度（本地更新）
   * 
   * @param amount 消费数量
   */
  const consumeQuota = (amount: number) => {
    quota.value.used += amount
    quota.value.remaining = Math.max(0, quota.value.remaining - amount)
  }

  /**
   * 检查是否有足够额度
   * 
   * @param required 需要的额度
   * @returns 是否足够
   */
  const hasEnoughQuota = (required: number): boolean => {
    return quota.value.remaining >= required
  }



  /**
   * 重置状态（用于登出）
   */
  const reset = () => {
    setDefaultGuestQuota()
    currentPackage.value = null
    error.value = null
    lastUpdated.value = 0
  }

  return {
    // 状态
    quota,
    currentPackage,
    isLoading,
    error,
    lastUpdated,
    usageInfo,
    packageSummary,
    entitlementConfig,

    // 计算属性
    isLoggedIn,
    percentage,
    isLow,
    isWarning,
    hasPaidPackage,
    tierLabel,
    quotaTypeLabel,
    singleRequestLimit,

    // 方法
    initialize,
    fetchAll,
    refresh,
    consumeQuota,
    hasEnoughQuota,
    reset
  }
})

